import { useState } from 'react'
import { User } from '../../types'

interface ResetPasswordModalProps {
  user: User
  onClose: () => void
  onReset: (userId: string, temporaryPassword: string) => void
}

export default function ResetPasswordModal({ user, onClose, onReset }: ResetPasswordModalProps) {
  const [temporaryPassword, setTemporaryPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (temporaryPassword.length < 6) {
      setError('Password must be at least 6 characters long')
      return
    }

    if (temporaryPassword !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    onReset(user.id, temporaryPassword)
  }

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity z-50">
      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
            <form onSubmit={handleSubmit}>
              <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                <h3 className="text-lg font-semibold leading-6 text-gray-900 mb-4">
                  Reset Password
                </h3>

                <div className="mb-4">
                  <p className="text-sm text-gray-600">
                    Reset password for <strong>{user.name}</strong> ({user.email})
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    The user will need to use this temporary password to login and set their own
                    password.
                  </p>
                </div>

                {error && (
                  <div className="rounded-md bg-red-50 p-3 mb-4">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                )}

                <div className="space-y-4">
                  <div>
                    <label
                      htmlFor="temp-password"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Temporary Password
                    </label>
                    <input
                      type="password"
                      id="temp-password"
                      required
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      placeholder="At least 6 characters"
                      value={temporaryPassword}
                      onChange={e => setTemporaryPassword(e.target.value)}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="confirm-password"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Confirm Password
                    </label>
                    <input
                      type="password"
                      id="confirm-password"
                      required
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                <button
                  type="submit"
                  className="inline-flex w-full justify-center rounded-md bg-orange-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-orange-500 sm:ml-3 sm:w-auto"
                >
                  Reset Password
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}
